// src/redux/reducer/ozonUnit/action/reducer.js
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { PATCH_COSTS_OZON_BY_SKU } from "./action";

dayjs.extend(customParseFormat);

// Храним даты безопасно для iOS/Safari и сервера.
const apiDateFormat = "YYYY-MM-DD";

function safeDayjs(value) {
  if (!value) return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }

  const s = String(value).trim();

  if (
    !s ||
    s === "Invalid Date" ||
    s === "undefined" ||
    s === "null"
  ) {
    return null;
  }

  const byDash = dayjs(s, "YYYY-MM-DD", true);
  if (byDash.isValid()) return byDash;

  const byDot = dayjs(s, "YYYY.MM.DD", true);
  if (byDot.isValid()) return byDot;

  const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoMatch) {
    const d = dayjs(isoMatch[1], "YYYY-MM-DD", true);
    return d.isValid() ? d : null;
  }

  return null;
}

function normalizeDateForStore(value) {
  const d = safeDayjs(value);
  return d ? d.format(apiDateFormat) : "";
}

function getDefaultRange() {
  return {
    date_from: dayjs().subtract(7, "day").format(apiDateFormat),
    date_to: dayjs().format(apiDateFormat),
  };
}

const defaultRange = getDefaultRange();

const initialState = {
  data: [],
  loading: false,
  error: null,
  date_from: defaultRange.date_from,
  date_to: defaultRange.date_to,
  isAscending: false,
  sortedField: "revenueTotal",

  // Серверная пагинация Ozon Unit
  totalElements: 0,
  totalPages: 1,
  currentPage: 1,
  pageSize: 20,
  totals: {},
};

const applyClientSort = (data, field, isAscending) => {
  if (!field) return data;

  const getVal = (obj, key) => {
    const v = obj?.[key];
    return v === undefined || v === null ? "" : v;
  };

  const arr = [...data];

  return arr.sort((a, b) => {
    const av = getVal(a, field);
    const bv = getVal(b, field);

    if (typeof av === "string" && typeof bv === "string") {
      return isAscending ? av.localeCompare(bv) : bv.localeCompare(av);
    }

    const an = Number(av);
    const bn = Number(bv);
    const bothNums = !Number.isNaN(an) && !Number.isNaN(bn);

    if (bothNums) {
      return isAscending ? an - bn : bn - an;
    }

    const as = String(av);
    const bs = String(bv);

    return isAscending ? as.localeCompare(bs) : bs.localeCompare(as);
  });
};

function extractStockPresent(row, source) {
  const present = row?.product?.stocks?.stocks?.find((s) => s.source === source)?.present;
  const n = Number(present);
  return Number.isFinite(n) ? n : 0;
}

function withDerivedStocks(data = []) {
  return data.map((row) => ({
    ...row,
    stockFboPresent: extractStockPresent(row, "fbo"),
    stockFbsPresent: extractStockPresent(row, "fbs"),
  }));
}

const ozonUnitReducer = (state = initialState, action) => {
  switch (action.type) {
    case "FETCH_OZON_BY_SKU_REQUEST":
      return { ...state, loading: true, error: null };

    case "FETCH_OZON_BY_SKU_SUCCESS": {
      const enriched = withDerivedStocks(action.payload.data);

      return {
        ...state,
        loading: false,
        data: enriched,

        totalElements: Number(action.payload.totalElements || 0),
        totalPages: Number(action.payload.totalPages || 1),
        currentPage: Number(action.payload.currentPage || 1),
        pageSize: Number(action.payload.pageSize || 20),
        totals: action.payload.totals || {},

        sortedField: action.payload?.sort?.field || state.sortedField || "revenueTotal",
        isAscending: (action.payload?.sort?.dir || "desc") === "asc",
      };
    }

    case "FETCH_OZON_BY_SKU_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload || "error",
      };

    case "SET_DATE_RANGE_OZON_BY_SKU": {
      const nextFrom = normalizeDateForStore(action.payload?.date_from);
      const nextTo = normalizeDateForStore(action.payload?.date_to);

      // ВАЖНО: не сохраняем Invalid Date и пустые даты.
      if (!nextFrom || !nextTo) {
        const def = getDefaultRange();
        return {
          ...state,
          date_from: def.date_from,
          date_to: def.date_to,
          data: [],
          totalElements: 0,
          totalPages: 1,
          currentPage: 1,
          totals: {},
        };
      }

      return {
        ...state,
        date_from: nextFrom,
        date_to: nextTo,
        data: [],
        totalElements: 0,
        totalPages: 1,
        currentPage: 1,
        totals: {},
      };
    }

    case "SORT_OZON_BY_SKU": {
      const { field } = action.payload || {};
      if (!field) return state;

      const nextAscending = state.sortedField === field ? !state.isAscending : false;

      // Сортировку делает сервер. Здесь только сохраняем состояние стрелки сортировки.
      return {
        ...state,
        sortedField: field,
        isAscending: nextAscending,
        currentPage: 1,
      };
    }

    case "SET_EMPTY_ARRAY_OZON_BY_SKU":
      return { ...state, data: [], totalElements: 0, totalPages: 1, currentPage: 1, totals: {} };

    case "RESET_OZON_BY_SKU":
      return { ...initialState };

    case "UPDATE_COST_PRICE_SKU": {
      const { sku, costPrice, amount, mode, costPriceEdited } = action.payload || {};
      const nextData = state.data.map((row) =>
        row.sku === sku
          ? { ...row, costPrice, amount, mode, costPriceEdited }
          : row
      );
      const sorted = applyClientSort(nextData, state.sortedField, state.isAscending);
      return { ...state, data: sorted };
    }

    case "UPDATE_COST_PRICE_ALL": {
      const { costPrice, amount, mode, costPriceEdited } = action.payload || {};
      const nextData = state.data.map((row) => ({
        ...row,
        costPrice,
        amount,
        mode,
        costPriceEdited,
      }));
      const sorted = applyClientSort(nextData, state.sortedField, state.isAscending);
      return { ...state, data: sorted };
    }

    case "UPDATE_ADPLAN_PERCENT_SKU": {
      const { sku, adPlanPercent, adPlanEdited } = action.payload || {};
      const next = state.data.map((row) =>
        row.sku === sku ? { ...row, adPlanPercent, adPlanEdited } : row
      );
      const sorted = applyClientSort(next, state.sortedField, state.isAscending);
      return { ...state, data: sorted };
    }

    case "UPDATE_ADPLAN_PERCENT_ALL": {
      const { adPlanPercent, adPlanEdited } = action.payload || {};
      const next = state.data.map((row) => ({
        ...row,
        adPlanPercent,
        adPlanEdited,
      }));
      const sorted = applyClientSort(next, state.sortedField, state.isAscending);
      return { ...state, data: sorted };
    }

    case "UPDATE_TAX_PERCENT_SKU": {
      const { sku, taxPercent, taxEdited } = action.payload || {};
      const next = state.data.map((row) =>
        row.sku === sku ? { ...row, taxPercent, taxEdited } : row
      );
      const sorted = applyClientSort(next, state.sortedField, state.isAscending);
      return { ...state, data: sorted };
    }

    case "UPDATE_TAX_PERCENT_ALL": {
      const { taxPercent, taxEdited } = action.payload || {};
      const next = state.data.map((row) => ({
        ...row,
        taxPercent,
        taxEdited,
      }));
      const sorted = applyClientSort(next, state.sortedField, state.isAscending);
      return { ...state, data: sorted };
    }

    case PATCH_COSTS_OZON_BY_SKU: {
      const updates = action.payload || [];
      if (!Array.isArray(updates) || updates.length === 0) return state;

      const bySku = new Map(
        updates.map((u) => [
          String(u.sku),
          {
            amount: Number(u.amount),
            mode: u.mode ?? "amount",
            costPriceEdited: u.costPriceEdited ?? true,
          },
        ])
      );

      const nextData = state.data.map((rec) => {
        if (!rec || rec.sku == null) return rec;

        const key = String(rec.sku);
        if (!bySku.has(key)) return rec;

        const { amount, mode, costPriceEdited } = bySku.get(key);
        const next = { ...rec, mode, costPriceEdited };

        if ("costPrice" in next) next.costPrice = amount;
        else if ("cost_price" in next) next.cost_price = amount;
        else if ("amount" in next) next.amount = amount;
        else next.costPrice = amount;

        return next;
      });

      const sorted = applyClientSort(nextData, state.sortedField, state.isAscending);
      return { ...state, data: sorted };
    }

    default:
      return state;
  }
};

export default ozonUnitReducer;

// селекторы
// Основной ключ — ozonUnit. Fallback ozonBySku оставлен только чтобы не упасть,
// если в store у тебя старое имя reducer-а. Лучше оставить один ключ: ozonUnit.
const selectOzonUnitState = (s) => s.ozonUnit ?? s.ozonBySku ?? initialState;

export const selectOzonRows = (s) => selectOzonUnitState(s).data;
export const selectOzonLoading = (s) => selectOzonUnitState(s).loading;
export const selectOzonError = (s) => selectOzonUnitState(s).error;
export const selectOzonDates = (s) => {
  const st = selectOzonUnitState(s);
  return {
    date_from: st.date_from,
    date_to: st.date_to,
  };
};
export const selectOzonSort = (s) => {
  const st = selectOzonUnitState(s);
  return {
    sortedField: st.sortedField,
    isAscending: st.isAscending,
  };
};

export const selectOzonPagination = (s) => {
  const st = selectOzonUnitState(s);
  return {
    totalElements: st.totalElements,
    totalPages: st.totalPages,
    currentPage: st.currentPage,
    pageSize: st.pageSize,
    totals: st.totals,
  };
};
